import { useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useOrganization } from "@/hooks/useOrganization";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, Save, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const DAYS = [1, 2, 3, 4, 5, 6, 0]; // Mon-Sun
const HOURS = Array.from({ length: 17 }, (_, i) => i + 6); // 06:00-22:00
const _SLOTS_PER_HOUR = 2; // 30-min slots

interface _SlotKey {
  day: number;
  hour: number;
  half: 0 | 1; // 0 = :00, 1 = :30
}

function slotToTime(hour: number, half: 0 | 1): string {
  return `${String(hour).padStart(2, "0")}:${half === 0 ? "00" : "30"}:00`;
}

function slotEndTime(hour: number, half: 0 | 1): string {
  const nextHalf = half === 0 ? 1 : 0;
  const nextHour = half === 0 ? hour : hour + 1;
  return `${String(nextHour).padStart(2, "0")}:${nextHalf === 0 ? "00" : "30"}:00`;
}

function slotKey(day: number, hour: number, half: 0 | 1): string {
  return `${day}-${hour}-${half}`;
}

export default function CoachAvailability() {
  const { t } = useTranslation("coach");
  const { user } = useAuth();
  const { organization } = useOrganization();
  const navigate = useNavigate();
  const qc = useQueryClient();

  // ── Load existing availability ──
  const { data: existing = [], isLoading } = useQuery({
    queryKey: ["coach-availability", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("coach_availability")
        .select("*")
        .eq("coach_id", user!.id);
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!user?.id,
  });

  // Convert DB rows to selected slot set
  const initialSelected = useMemo(() => {
    const set = new Set<string>();
    for (const row of existing) {
      if (!row.is_available) continue;
      const startH = parseInt((row.start_time as string).slice(0, 2));
      const startM = parseInt((row.start_time as string).slice(3, 5));
      const endH = parseInt((row.end_time as string).slice(0, 2));
      const endM = parseInt((row.end_time as string).slice(3, 5));
      // Fill all 30-min slots in this range
      let h = startH, m = startM;
      while (h < endH || (h === endH && m < endM)) {
        set.add(slotKey(row.day_of_week, h, m === 0 ? 0 : 1));
        m += 30;
        if (m >= 60) { h++; m = 0; }
      }
    }
    return set;
  }, [existing]);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [initialized, setInitialized] = useState(false);

  // Sync initial data once loaded
  if (!initialized && !isLoading && existing.length >= 0) {
    setSelected(new Set(initialSelected));
    setInitialized(true);
  }

  const toggleSlot = useCallback((day: number, hour: number, half: 0 | 1) => {
    const key = slotKey(day, hour, half);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const hasChanges = useMemo(() => {
    if (selected.size !== initialSelected.size) return true;
    for (const k of selected) if (!initialSelected.has(k)) return true;
    return false;
  }, [selected, initialSelected]);

  // ── Save mutation ──
  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!user || !organization) throw new Error("Not logged in");
      // Delete all existing
      await supabase.from("coach_availability").delete().eq("coach_id", user.id);

      // Group selected slots by day into contiguous ranges
      const rows: { coach_id: string; organization_id: string; day_of_week: number; start_time: string; end_time: string; is_available: boolean }[] = [];

      for (const day of DAYS) {
        let rangeStart: string | null = null;
        let rangeEnd: string | null = null;

        for (const hour of HOURS) {
          for (const half of [0, 1] as const) {
            const key = slotKey(day, hour, half);
            const start = slotToTime(hour, half);
            const end = slotEndTime(hour, half);

            if (selected.has(key)) {
              if (rangeStart === null) rangeStart = start;
              rangeEnd = end;
            } else {
              if (rangeStart !== null && rangeEnd !== null) {
                rows.push({ coach_id: user.id, organization_id: organization.id, day_of_week: day, start_time: rangeStart, end_time: rangeEnd, is_available: true });
                rangeStart = null;
                rangeEnd = null;
              }
            }
          }
        }
        // Close final range
        if (rangeStart !== null && rangeEnd !== null) {
          rows.push({ coach_id: user.id, organization_id: organization.id, day_of_week: day, start_time: rangeStart, end_time: rangeEnd, is_available: true });
        }
      }

      if (rows.length > 0) {
        const { error } = await supabase.from("coach_availability").insert(rows);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      toast.success(t("availability.saved"));
      qc.invalidateQueries({ queryKey: ["coach-availability"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const clearAll = () => {
    if (window.confirm(t("availability.clearConfirm"))) {
      setSelected(new Set());
    }
  };

  const dayLabels: Record<number, string> = { 1: t("availability.mon"), 2: t("availability.tue"), 3: t("availability.wed"), 4: t("availability.thu"), 5: t("availability.fri"), 6: t("availability.sat"), 0: t("availability.sun") };

  if (isLoading) return <div className="max-w-lg lg:max-w-5xl mx-auto space-y-4"><Skeleton className="h-10" /><Skeleton className="h-96" /></div>;

  return (
    <div className="max-w-2xl lg:max-w-5xl mx-auto space-y-4 pb-20">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => navigate("/coach")}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-lg font-semibold flex-1">{t("availability.title")}</h1>
        <Button variant="outline" size="sm" className="gap-1" onClick={clearAll}>
          <Trash2 className="h-3.5 w-3.5" /> {t("availability.clear")}
        </Button>
        <Button size="sm" className="gap-1" onClick={() => saveMutation.mutate()} disabled={!hasChanges || saveMutation.isPending}>
          {saveMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
          {t("availability.save")}
        </Button>
      </div>

      <p className="text-xs text-muted-foreground">{t("availability.hint")}</p>

      {/* Grid */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <div className="min-w-[600px]">
            {/* Day headers */}
            <div className="grid grid-cols-[60px_repeat(7,1fr)] border-b sticky top-0 bg-card z-10">
              <div className="p-2 text-xs text-muted-foreground" />
              {DAYS.map((d) => (
                <div key={d} className="p-2 text-center text-xs font-medium">{dayLabels[d]}</div>
              ))}
            </div>

            {/* Time rows */}
            {HOURS.map((hour) => (
              <div key={hour} className="grid grid-cols-[60px_repeat(7,1fr)]">
                {/* Time label */}
                <div className="flex items-start justify-end pr-2 pt-0.5 text-[10px] text-muted-foreground h-10 border-r">
                  {String(hour).padStart(2, "0")}:00
                </div>
                {/* Day cells (2 half-hour slots stacked) */}
                {DAYS.map((day) => (
                  <div key={day} className="flex flex-col border-r border-b h-10">
                    {([0, 1] as const).map((half) => {
                      const key = slotKey(day, hour, half);
                      const isSelected = selected.has(key);
                      return (
                        <button
                          key={half}
                          className={cn(
                            "flex-1 transition-colors border-b last:border-0",
                            isSelected
                              ? "bg-primary/20 hover:bg-primary/30"
                              : "hover:bg-muted/50"
                          )}
                          onClick={() => toggleSlot(day, hour, half)}
                        />
                      );
                    })}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
